import clsx from "clsx";
import { useTheme } from "next-themes";
import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import { findCPOnTrade } from "../../tradeLookupHelpers";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import { FlashKeyframes, getFlashStyles } from "@/lib/styles/flashStyles";
import {
  calculateIcebergPercentForER,
  isIcebergEditableForCP,
} from "./icebergHelpers";

/**
 * IcebergCellRenderer (LQC-922 / LQC-1085)
 *
 * Renders the per-CP iceberg (`releaseSize`) on the OP ER row as centered
 * "{N}%" text. Subscribes to tradeDataStore so the cell reactively reflects
 * MegaPipe updates after a PATCH /Trade/Execution/Settings.
 *
 * The rounded-pill styling (LQC-1085) is applied here in the renderer rather
 * than via colDef `cellClass`. AG Grid only re-runs `cellClass` on cell
 * creation/refresh, so a Zustand-driven status flip (e.g. Quoted → Affirmed
 * mutating the CP in place) left the pill class glued to the DOM. Driving
 * the className from this renderer's Zustand subscription means the pill
 * appears/disappears in lockstep with status transitions.
 *
 * Returns an empty container when releaseSize is missing — visibility of the
 * column itself is gated upstream by `tradeHasIceberg`.
 */
const IcebergCellRenderer = (params: any) => {
  const { theme } = useTheme();
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade = currentTradeData.find(
    (trade) => trade.esid == fullTradeData?.esid,
  );

  const liveCP = findCPOnTrade(thisTrade, params.data?.executionRequestId);
  const cp = liveCP ?? params.data;

  const percent = calculateIcebergPercentForER(cp);
  const editable = isIcebergEditableForCP(cp, thisTrade ?? fullTradeData);

  // LQC-1040: subscribe to the per-ER iceberg flash array so a rejected
  // reduction in the editor briefly flashes this cell red.
  const flashEntries = tradeSettingsStore(
    (state) => state.icebergErCellsToFlash,
  );
  const isFlashing = flashEntries.some(
    (f) =>
      f.esid === fullTradeData?.esid &&
      f.executionRequestId == params.data?.executionRequestId &&
      f.field === "releaseSize",
  );

  if (percent === null) {
    return (
      <div
        data-testid="IcebergCellContainer-empty"
        id="IcebergCellContainer-empty"
      />
    );
  }

  const pillClass = editable
    ? theme === "light"
      ? "inputCellOtherLight roundedInputCell"
      : "inputCellOtherDark roundedInputCell"
    : "";

  return (
    <>
      <FlashKeyframes isFlashing={isFlashing} />
      <div
        data-testid="IcebergCellContainer"
        id="IcebergCellContainer"
        className={clsx(
          "flex h-full w-full items-center justify-center",
          pillClass,
        )}
        style={isFlashing ? getFlashStyles(true) : undefined}
      >
        {Math.round(percent)}%
      </div>
    </>
  );
};

export default IcebergCellRenderer;
